import { BPButton } from "@/components/bp/bpbutton";
import React, { useEffect, useState } from "react";
import { Button, Input, Icon } from "@/components/bp/bp-ui";

export interface EditableTextProps {
  value?: string | null;
  placeholder?: string;
  canEdit?: boolean;
  onSave?: (newValue: string) => Promise<void> | void;
  className?: string;
  inputProps?: any;
  saveLabel?: string;
  cancelLabel?: string;
  editAriaLabel?: string;
}

/**
 * Reusable inline editable text component.
 * - Renders text when not editing, with an edit button if canEdit.
 * - When editing, shows an Input and Save / Cancel buttons.
 * - Calls onSave (can be async). Shows a loading state while saving.
 */
export const EditableText: React.FC<EditableTextProps> = ({
  value = "",
  placeholder,
  canEdit = true,
  onSave,
  className,
  inputProps,
  saveLabel,
  cancelLabel,
  editAriaLabel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(value || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditingValue(value || "");
  }, [value]);

  const startEdit = () => {
    setError(null);
    setEditingValue(value || "");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setError(null);
    setEditingValue(value || "");
    setIsEditing(false);
  };

  const save = async () => {
    if (!onSave) {
      setIsEditing(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onSave(editingValue);
      setIsEditing(false);
    } catch (err: any) {
      console.error("EditableText save error:", err);
      setError(err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const tSave = saveLabel ?? window.translation?.common?.save ?? "Save";
  const tCancel = cancelLabel ?? window.translation?.common?.cancel ?? "Cancel";
  const tEditAria = editAriaLabel ?? window.translation?.common?.edit ?? "Edit";

  return (
    <div className={className} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {!isEditing && (
        <>
          <span style={{ whiteSpace: "pre-wrap" }}>{value || placeholder || ""}</span>
          {canEdit && (
            <BPButton icon compact size="mini" onClick={startEdit} aria-label={tEditAria}>
              <Icon name="edit" />
            </BPButton>
          )}
        </>
      )}

      {isEditing && (
        <>
          <Input
            value={editingValue}
            onChange={(e, data) => setEditingValue((data && (data.value as string)) ?? (e.target as HTMLInputElement).value)}
            placeholder={placeholder ?? ""}
            disabled={loading}
            {...inputProps}
            style={{ minWidth: 260 }}
          />
          <BPButton primary compact size="mini" onClick={save} loading={loading} disabled={loading}>
            {tSave}
          </BPButton>
          <BPButton compact size="mini" onClick={cancelEdit} disabled={loading}>
            {tCancel}
          </BPButton>
          {error && <div style={{ color: "var(--danger-color, #9f3a38)", marginLeft: 8 }}>{error}</div>}
        </>
      )}
    </div>
  );
};

export default EditableText;